import Badge from "~/v2/components/ui/badge"
import Button from "~/v2/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/v2/components/ui/card"
import { getVaasStatus } from "~/v2/lib/vaas-mapper"
import type { VaasCourse } from "~/v2/types/vaas"

const statusVariantMap = {
  archived: "warning",
  draft: "muted",
  published: "success",
  unpublished: "muted",
} as const satisfies Record<
  ReturnType<typeof getVaasStatus>,
  "muted" | "success" | "warning"
>

type VaasHeaderProps = {
  actionError?: string
  course: VaasCourse
  isDirty?: boolean
  onOpenHistoryDialog: () => void
  onOpenVersionDialog: () => void
  onPublish: () => void
  onSave: () => void
  onSaveVaasCourse: () => void
  onUnpublish: () => void
  publishLoading?: boolean
  saveError?: string
  saveLoading?: boolean
  unpublishLoading?: boolean
  vaasCourseSaveLoading?: boolean
  versionLoading?: boolean
}

const VaasHeader = ({
  actionError,
  course,
  isDirty,
  onOpenHistoryDialog,
  onOpenVersionDialog,
  onPublish,
  onSave,
  onSaveVaasCourse,
  onUnpublish,
  publishLoading,
  saveError,
  saveLoading,
  unpublishLoading,
  vaasCourseSaveLoading,
  versionLoading,
}: VaasHeaderProps) => {
  const status = getVaasStatus(course)
  const canPublish = status === "draft" || status === "unpublished"
  const canUnpublish = status === "published"
  const hasMessages = Boolean(saveError || actionError)
  const tld = import.meta.env.VITE_ENV === "production" ? "com" : "dev"

  return (
    <Card>
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-3 flex flex-wrap gap-2">
              <Badge variant={statusVariantMap[status]}>{status}</Badge>
              <Badge>Cont. ID {course.container_id}</Badge>
              {course.vaas_id && (
                <Badge variant="success">VAAS ID {course.vaas_id}</Badge>
              )}
            </div>
            <CardTitle className="text-3xl">{course.name}</CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>
                Updated {course.updated?.format?.("M/D/YYYY") || "Unknown"}.
              </span>
              <button
                className="font-medium text-primary underline-offset-4 hover:underline"
                onClick={onOpenHistoryDialog}
                type="button"
              >
                Version {course.version}
              </button>
            </div>

            {(course.vaas_id && course.published) && (
              <a
                className="inline-block text-sm text-primary underline-offset-4 hover:underline"
                href={`https://video.dataprotocol.${tld}/embed/${course.vaas_id}`}
                rel="noreferrer"
                target="_blank"
              >
                Open live VAAS link
              </a>
            )}
            {course.vaas_id && <br />}
            {course.vaas_id && (
              <a
                className="inline-block text-sm text-primary underline-offset-4 hover:underline"
                href={`https://video.dataprotocol.${tld}/embed/${course.vaas_id}-${course.id}`}
                rel="noreferrer"
                target="_blank"
              >
                Open preview VAAS link
              </a>
            )}
          </div>
          <div className="flex flex-wrap gap-2 lg:max-w-md lg:justify-end">
            <Button
              disabled={saveLoading || !isDirty}
              onClick={onSave}
              variant="outline"
            >
              {saveLoading ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={
                vaasCourseSaveLoading ||
                publishLoading ||
                unpublishLoading ||
                versionLoading
              }
              onClick={onSaveVaasCourse}
              variant="outline"
            >
              {vaasCourseSaveLoading
                ? "Updating VAAS course..."
                : "Update VAAS course"}
            </Button>
            {canPublish && (
              <Button
                disabled={
                  saveLoading ||
                  publishLoading ||
                  unpublishLoading ||
                  vaasCourseSaveLoading
                }
                onClick={onPublish}
                variant="outline"
              >
                {publishLoading ? "Publishing..." : "Publish"}
              </Button>
            )}
            {canUnpublish && (
              <Button
                disabled={
                  saveLoading ||
                  publishLoading ||
                  unpublishLoading ||
                  vaasCourseSaveLoading
                }
                onClick={onUnpublish}
                variant="outline"
              >
                {unpublishLoading ? "Unpublishing..." : "Unpublish"}
              </Button>
            )}
            <Button
              disabled={
                isDirty ||
                saveLoading ||
                publishLoading ||
                unpublishLoading ||
                vaasCourseSaveLoading ||
                versionLoading
              }
              onClick={onOpenVersionDialog}
              variant="outline"
            >
              {versionLoading ? "Creating version..." : "New version"}
            </Button>
          </div>
        </div>
      </CardHeader>
      {hasMessages && (
        <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
          {saveError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive">
              {saveError}
            </div>
          )}
          {actionError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive">
              {actionError}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

export default VaasHeader
